import {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import { getSave, touchSave } from '../account/accountApi';
import { fetchClub, fetchClubFinances, fetchSquad } from './gameApi';

// Drives a single in-club session from a SAVE. The save (owned by the user,
// in the game project) holds the club_id; the club/finances/squad come live
// from the editor world. status: loading | ready | notfound | empty | error.
const ClubContext = createContext(null);

export function useClub() {
  const ctx = useContext(ClubContext);
  if (!ctx) throw new Error('useClub must be used inside <ClubProvider>');
  return ctx;
}

export function ClubProvider({ saveId, children }) {
  const [save, setSave] = useState(null);
  const [club, setClub] = useState(null);
  const [finances, setFinances] = useState(null);
  const [squad, setSquad] = useState(null);
  const [status, setStatus] = useState('loading');

  const load = useCallback(async (id) => {
    if (!id) { setStatus('notfound'); return; }
    setStatus('loading');
    const { data: sv, error: svErr } = await getSave(id);
    if (svErr) { setStatus('error'); return; }
    if (!sv) { setStatus('notfound'); return; }
    setSave(sv);
    const [c, f, s] = await Promise.all([
      fetchClub(sv.club_id), fetchClubFinances(sv.club_id), fetchSquad(sv.club_id),
    ]);
    if (c.error) { setStatus('error'); return; }
    if (!c.data) { setStatus('empty'); return; }
    setClub(c.data);
    setFinances(f && f.data ? f.data : null);
    setSquad(s && s.data ? s.data : []);
    setStatus('ready');
    touchSave(id); // mark as recently played; fire and forget
  }, []);

  useEffect(() => { load(saveId); }, [saveId, load]);

  const value = { saveId, save, club, finances, squad, status, reload: () => load(saveId) };
  return <ClubContext.Provider value={value}>{children}</ClubContext.Provider>;
}
